import { useState } from "react";
import { JobFormValues } from "../schemas/jobFormSchema";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from '@/contexts/AuthContext';

interface UseJobSubmitProps {
  clientId?: string;
  onSuccess?: () => void;
  onClose: () => void;
}

export const useJobSubmit = ({ clientId, onSuccess, onClose }: UseJobSubmitProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { session } = useAuth();

  const onSubmit = async (data: JobFormValues) => {
    try {
      setIsSubmitting(true);
      
      if (!session?.user?.id) {
        throw new Error("You must be logged in to create a job");
      }
      
      const { data: job, error: jobError } = await supabase
        .from("jobs")
        .insert({
          title: data.title,
          company: data.company,
          location: data.location,
          type: data.type,
          salary: data.salary,
          status: data.status,
          description: data.description,
          requirements: data.requirements,
          document_url: data.document_url,
          source_url: data.source_url,
          user_id: session.user.id // Set the user_id from the session
        })
        .select()
        .single();

      if (jobError) throw jobError;

      if (clientId && job) {
        const { error: linkError } = await supabase
          .from("jobs_clients")
          .insert({
            job_id: job.id,
            client_id: clientId,
          });

        if (linkError) throw linkError;
      }

      toast.success("Job created successfully");
      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Error creating job:", error);
      toast.error("Failed to create job");
    } finally {
      setIsSubmitting(false);
    }
  };

  return { onSubmit, isSubmitting };
};
